import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

type AnyClient = { from: (t: string) => any };

const QuerySchema = z.object({
  q: z.string().trim().max(80).nullish(),
  categories: z.array(z.string().max(40)).max(12).nullish(),
  location: z.string().trim().max(80).nullish(),
  minPrice: z.number().int().positive().nullish(),
  maxPrice: z.number().int().positive().nullish(),
  saleKind: z.enum(["fixed", "auction"]).nullish(),
  sellerType: z.enum(["private", "business"]).nullish(),
});

/** Hur många bevakningar varje nivå får ha. */
export const WATCH_LIMITS: Record<string, number | null> = {
  free: 3,
  plus: 15,
  pro: null,
  expert: null,
};

export const listMySavedSearches = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const client = context.supabase as unknown as AnyClient;
    const [{ data: rows }, { data: plan }] = await Promise.all([
      client
        .from("saved_searches")
        .select("id, label, query, notify_channel, paused, last_notified_at, created_at")
        .eq("user_id", context.userId)
        .order("created_at", { ascending: false }),
      client.from("user_plans").select("plan").eq("user_id", context.userId).maybeSingle(),
    ]);
    const planId = (plan?.plan ?? "free") as string;
    return {
      plan: planId,
      limit: WATCH_LIMITS[planId] ?? null,
      searches: (rows ?? []) as any[],
    };
  });

export const createSavedSearch = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        label: z.string().trim().min(1).max(60),
        query: QuerySchema,
        notifyChannel: z.enum(["push", "email", "both"]).default("both"),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const client = context.supabase as unknown as AnyClient;
    const { data: plan } = await client
      .from("user_plans")
      .select("plan")
      .eq("user_id", context.userId)
      .maybeSingle();
    const limit = WATCH_LIMITS[(plan?.plan ?? "free") as string] ?? null;
    if (limit !== null) {
      const { count } = await client
        .from("saved_searches")
        .select("id", { count: "exact", head: true })
        .eq("user_id", context.userId);
      if ((count ?? 0) >= limit) {
        throw new Error(
          `Din nivå tillåter ${limit} bevakningar. Uppgradera för fler, eller ta bort en gammal.`,
        );
      }
    }

    const { data: row, error } = await client
      .from("saved_searches")
      .insert({
        user_id: context.userId,
        label: data.label,
        query: data.query,
        notify_channel: data.notifyChannel,
      })
      .select("id")
      .single();
    if (error) throw new Error("Kunde inte spara bevakningen.");
    return { id: row.id as string };
  });

export const updateSavedSearch = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        label: z.string().trim().min(1).max(60).optional(),
        paused: z.boolean().optional(),
        notifyChannel: z.enum(["push", "email", "both"]).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const client = context.supabase as unknown as AnyClient;
    const patch: Record<string, unknown> = {};
    if (data.label !== undefined) patch.label = data.label;
    if (data.paused !== undefined) patch.paused = data.paused;
    if (data.notifyChannel !== undefined) patch.notify_channel = data.notifyChannel;
    const { error } = await client
      .from("saved_searches")
      .update(patch)
      .eq("id", data.id)
      .eq("user_id", context.userId);
    if (error) throw new Error("Kunde inte uppdatera bevakningen.");
    return { ok: true };
  });

export const deleteSavedSearch = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const client = context.supabase as unknown as AnyClient;
    await client
      .from("saved_searches")
      .delete()
      .eq("id", data.id)
      .eq("user_id", context.userId);
    return { ok: true };
  });
